import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Loader2, FileText, Upload, Download, Trash2, Plus, Paperclip, Image as ImageIcon } from 'lucide-react';
import { useFormStatusFlows, FORM_STATUS_STYLES, formStatusLabel } from '@/hooks/useFormStatusFlows';
import { useAuth } from '@/context/AuthContext';

/**
 * AllFilesTab — hoja "All Files" del Excel "Office Work Flow (2)".
 * Header fijo (OWF# · Ref# / Customer · Well Site Address) + lista en orden de
 * ejecución con columnas Nombre de forma · Date · Status · Up-load PDF or Pic,
 * más el botón "New Form". Solo aparecen las formas que SE HICIERON.
 */
export default function AllFilesTab({ permit, customerName, onOpenForm, onRefresh }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const { flowFor } = useFormStatusFlows();
  const permitId = permit?.id;
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [rowTarget, setRowTarget] = useState(null);
  const fileInputRef = useRef(null);
  const rowInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!permitId) return;
    setLoading(true);
    try {
      const [filesRes, tplRes] = await Promise.all([
        api.get(`/permits/${permitId}/all-files`),
        api.get('/custom-forms/templates').catch(() => ({ data: [] })),
      ]);
      setItems(filesRes.data || []);
      setTemplates((tplRes.data || []).filter(x => x.is_active !== false));
    } catch {
      toast.error(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [permitId, t]);

  useEffect(() => { load(); }, [load]);

  const flowIdFor = (item) => {
    if (item.kind === 'structured') {
      return item.form_key === 'well_application' ? 'structured_permit_application' : 'structured_completion_report';
    }
    return item.template_id;
  };

  const statusLabel = (item) => {
    const s = flowFor(flowIdFor(item)).find(x => x.key === item.status);
    return s ? formStatusLabel(s) : (item.status || '—');
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'permit');
      formData.append('entity_id', permitId);
      formData.append('category', 'other');
      formData.append('status', 'draft');
      formData.append('description', uploadName.trim() || file.name);
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('permits.allFiles.uploaded', 'Archivo subido'));
      setUploadName('');
      load();
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Columna "Up-load PDF or Pic": adjunta un PDF o una foto A ESA FILA.
  const handleRowUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !rowTarget) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'permit');
      formData.append('entity_id', permitId);
      formData.append('doc_type', 'form_attachment');
      formData.append('category', rowTarget.id);
      formData.append('description', file.name);
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Adjunto agregado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setUploading(false);
      setRowTarget(null);
      if (rowInputRef.current) rowInputRef.current.value = '';
    }
  };

  const triggerRowUpload = (item) => { setRowTarget(item); setTimeout(() => rowInputRef.current?.click(), 0); };

  const handleStatusChange = async (item, newStatus) => {
    try {
      if (item.kind === 'upload') {
        await api.put(`/files/${item.file_id}/status`, { status: newStatus });
      } else if (item.kind === 'structured') {
        const pathPart = item.form_key === 'well_application' ? 'well-application' : 'completion-report';
        await api.put(`/permits/${permitId}/${pathPart}/status`, { status: newStatus });
      } else {
        await api.put(`/custom-forms/instances/${item.instance_id}`, { status: newStatus });
      }
      load();
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      if (item.kind === 'upload') {
        await api.delete(`/files/${item.file_id}`);
      } else if (item.kind === 'structured') {
        const pathPart = item.form_key === 'well_application' ? 'well-application' : 'completion-report';
        await api.delete(`/permits/${permitId}/${pathPart}`);
      } else {
        await api.delete(`/custom-forms/instances/${item.instance_id}`);
      }
      toast.success(t('success.deleted'));
      load();
      onRefresh && onRefresh();
    } catch {
      toast.error(t('errors.deletingError'));
    }
  };

  const handleOpen = (item) => {
    if (item.kind === 'upload') {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${item.file_id}`, '_blank');
      return;
    }
    onOpenForm && onOpenForm(item.kind === 'structured'
      ? { kind: 'structured', formKey: item.form_key, name: item.name }
      : { kind: 'template', templateId: item.template_id, instanceId: item.instance_id, name: item.name, status: item.status });
  };

  const handleNewForm = (tpl) => {
    const structuredKey = tpl.id === 'structured_permit_application' ? 'well_application'
      : tpl.id === 'structured_completion_report' ? 'completion_report' : null;
    onOpenForm && onOpenForm(structuredKey
      ? { kind: 'structured', formKey: structuredKey, name: tpl.name }
      : { kind: 'template', templateId: tpl.id, instanceId: null, name: tpl.name, status: 'draft' });
  };

  return (
    <div className="space-y-3 py-1" data-testid="all-files-tab">
      {/* New Form + upload suelto */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="all-files-actions">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="bg-[#003366] hover:bg-[#004080] text-white h-9 text-xs" data-testid="all-files-new-form-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Form
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
            {templates.map(tpl => (
              <DropdownMenuItem key={tpl.id} onClick={() => handleNewForm(tpl)} data-testid={`all-files-new-form-${tpl.id}`}>
                <FileText className="w-3.5 h-3.5 mr-2 text-[#003366]" /> {tpl.name}
              </DropdownMenuItem>
            ))}
            {templates.length === 0 && <DropdownMenuItem disabled>{t('customForms.noTemplates', 'Sin formas')}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>

        <Input
          value={uploadName}
          onChange={e => setUploadName(e.target.value)}
          placeholder={t('permits.allFiles.namePlaceholder', 'Nombre del archivo (ej. Site Plan)')}
          className="h-9 text-sm flex-1 min-w-[180px]"
          data-testid="all-files-upload-name"
        />
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*" onChange={handleUpload} className="hidden" data-testid="all-files-file-input" />
        <input ref={rowInputRef} type="file" accept="application/pdf,image/*" onChange={handleRowUpload} className="hidden" data-testid="all-files-row-file-input" />
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="h-9 text-xs border-[#003366]/20 text-[#003366]" data-testid="all-files-upload-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          {t('permits.allFiles.uploadBtn', 'Upload PDF / Pic')}
        </Button>
      </div>

      {/* Tabla: Nombre de forma | Date | Status | Up-load PDF or Pic */}
      {loading ? (
        <div className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-8" data-testid="all-files-empty">
          <FileText className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{t('permits.allFiles.empty', 'Todavía no se hizo ninguna forma')}</p>
        </div>
      ) : (
        <>
        {/* Desktop / tablet: tabla */}
        <div className="hidden md:block border border-gray-200 rounded-lg overflow-hidden" data-testid="all-files-list">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">Form</th>
                <th className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 w-28">Date</th>
                <th className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 w-44">Status</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 w-40">Up-load PDF or Pic</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const flow = flowFor(flowIdFor(item));
                return (
                  <tr key={item.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors" data-testid={`all-files-item-${item.id}`}>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => handleOpen(item)} className="flex items-center gap-2 text-left group" data-testid={`all-files-open-${item.id}`}>
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-800 group-hover:text-[#003366] group-hover:underline">{item.name}</span>
                      </button>
                      {(item.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1 pl-6">
                          {item.attachments.map(a => (
                            <button
                              key={a.file_id}
                              type="button"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${a.file_id}`, '_blank')}
                              className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-200"
                              data-testid={`all-files-attachment-${a.file_id}`}
                            >
                              {a.file_type === 'image' ? <ImageIcon className="w-2.5 h-2.5" /> : <Paperclip className="w-2.5 h-2.5" />}
                              <span className="truncate max-w-[140px]">{a.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap" data-testid={`all-files-date-${item.id}`}>
                      {item.date ? new Date(item.date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Select value={item.status || 'draft'} onValueChange={v => handleStatusChange(item, v)}>
                        <SelectTrigger className={`h-7 w-full text-[11px] border ${FORM_STATUS_STYLES[item.status] || FORM_STATUS_STYLES['']}`} data-testid={`all-files-status-${item.id}`}>
                          <SelectValue>{statusLabel(item)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {flow.map(s => <SelectItem key={s.key} value={s.key}>{formStatusLabel(s)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => triggerRowUpload(item)} disabled={uploading} data-testid={`all-files-row-upload-${item.id}`}>
                          <Upload className="w-3 h-3 mr-1" /> Upload
                        </Button>
                        {item.kind === 'upload' && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleOpen(item)} data-testid={`all-files-download-${item.id}`}>
                            <Download className="w-3.5 h-3.5 text-gray-500" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(item)} data-testid={`all-files-delete-${item.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile — lista vertical de cards (Alian: "All Files como lista
            vertical" en el celular, sin tabla cortada). */}
        <div className="md:hidden space-y-2.5" data-testid="all-files-list-mobile">
          {items.map(item => {
            const flow = flowFor(flowIdFor(item));
            return (
              <div key={item.id} className="border border-gray-200 rounded-lg p-3 space-y-2.5" data-testid={`all-files-card-${item.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <button type="button" onClick={() => handleOpen(item)} className="flex items-start gap-2 text-left flex-1 min-w-0" data-testid={`all-files-open-mobile-${item.id}`}>
                    <FileText className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-gray-800">{item.name}</span>
                      <p className="text-[11px] text-gray-400">{item.date ? new Date(item.date).toLocaleDateString() : '—'}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.kind === 'upload' && (
                      <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => handleOpen(item)} data-testid={`all-files-download-mobile-${item.id}`}>
                        <Download className="w-4 h-4 text-gray-500" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => handleDelete(item)} data-testid={`all-files-delete-mobile-${item.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
                {(item.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pl-6">
                    {item.attachments.map(a => (
                      <button
                        key={a.file_id}
                        type="button"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${a.file_id}`, '_blank')}
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"
                        data-testid={`all-files-attachment-mobile-${a.file_id}`}
                      >
                        {a.file_type === 'image' ? <ImageIcon className="w-2.5 h-2.5" /> : <Paperclip className="w-2.5 h-2.5" />}
                        <span className="truncate max-w-[140px]">{a.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Select value={item.status || 'draft'} onValueChange={v => handleStatusChange(item, v)}>
                    <SelectTrigger className={`h-10 flex-1 text-xs border ${FORM_STATUS_STYLES[item.status] || FORM_STATUS_STYLES['']}`} data-testid={`all-files-status-mobile-${item.id}`}>
                      <SelectValue>{statusLabel(item)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {flow.map(s => <SelectItem key={s.key} value={s.key}>{formStatusLabel(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-10 text-xs px-3 flex-shrink-0" onClick={() => triggerRowUpload(item)} disabled={uploading} data-testid={`all-files-row-upload-mobile-${item.id}`}>
                    <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
